// WHAT: React hook for managing Miracle Flow session state and operations
// WHY:  Provides state management for session lifecycle (start, complete, stop) with database persistence
// DOMAIN: Productivity → Miracle Flow → Session Management
// DEPENDENCIES: sessionApi service, React hooks, session types
// RISKS:
//   - NOTE: Session refresh logic checks both database and local state - may cause sync issues
//   - NOTE: Step completion is asynchronous - may not reflect immediately in UI
//   - WARN: Error handling may not provide actionable feedback for session failures
//   - NOTE: Session status polling may not handle rapid state changes correctly

import type { Session, SessionStatus } from "../types";

import { useState, useEffect, useCallback } from "react";

import { sessionApi } from "../utils/api-client";

export interface UseSessionReturn {
  completeStep: (step: string) => Promise<void>;
  error: null | string;
  finishSession: () => Promise<void>;
  isLoading: boolean;
  refreshSession: () => Promise<null | Session>;
  session: null | Session;
  sessionStatus: null | SessionStatus;
  startEveningSession: () => Promise<null | Session>;
  startMorningSession: () => Promise<null | Session>;
  stopSession: () => Promise<void>;
}

export const useSession = (): UseSessionReturn => {
  const [session, setSession] = useState<null | Session>(null);
  const [sessionStatus, setSessionStatus] = useState<null | SessionStatus>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);

  const refreshSession = useCallback(async (): Promise<null | Session> => {
    try {
      setError(null);

      // First try to load any active session from database
      const activeSession = await sessionApi.loadActiveSession();
      if (activeSession) {
        setSession(activeSession);
        setSessionStatus({
          has_active_session: true,
          session: activeSession,
        });
        return activeSession;
      }

      // If no active session found, try local session
      const [currentSession, status] = await Promise.all([
        sessionApi.getCurrentSession(),
        sessionApi.getSessionStatus(),
      ]);

      setSession(currentSession);
      setSessionStatus(status);
      return currentSession;
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : "Failed to refresh session");
      return null;
    }
  }, []);

  const startMorningSession = useCallback(async (): Promise<null | Session> => {
    try {
      setIsLoading(true);
      setError(null);

      await sessionApi.startMorningSession();
      return await refreshSession();
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : "Failed to start morning session");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [refreshSession]);

  const startEveningSession = useCallback(async (): Promise<null | Session> => {
    try {
      setIsLoading(true);
      setError(null);

      await sessionApi.startEveningSession();
      return await refreshSession();
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : "Failed to start evening session");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [refreshSession]);

  const completeStep = useCallback(
    async (step: string) => {
      try {
        setError(null);

        await sessionApi.completeStep(step);
        await refreshSession();
      } catch (error_) {
        setError(error_ instanceof Error ? error_.message : "Failed to complete step");
      }
    },
    [refreshSession],
  );

  const finishSession = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      await sessionApi.finishSession();
      await refreshSession();
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : "Failed to finish session");
    } finally {
      setIsLoading(false);
    }
  }, [refreshSession]);

  const stopSession = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      await sessionApi.stopSession();
      await refreshSession();
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : "Failed to stop session");
    } finally {
      setIsLoading(false);
    }
  }, [refreshSession]);

  // Load session on mount
  useEffect(() => {
    const loadActiveSession = async () => {
      try {
        // First try to load any active session from database
        const activeSession = await sessionApi.loadActiveSession();
        if (activeSession) {
          setSession(activeSession);
          return;
        }

        // If no active session found, refresh normally
        await refreshSession();
      } catch {
        // Error loading active session – fallback to normal refresh
        await refreshSession();
      }
    };

    void loadActiveSession();
  }, [refreshSession]);

  return {
    completeStep,
    error,
    finishSession,
    isLoading,
    refreshSession,
    session,
    sessionStatus,
    startEveningSession,
    startMorningSession,
    stopSession,
  };
};
